import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { List, Switch } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';

import getGlobalStyles from '../globalStyles';
import { useThemeSettings } from '../ThemeProvider';
import { useUserStore } from '../store/userStore';
import { signOutUser, bootstrapAuth } from '../services/authService';
import { ROUTES } from '../shared/constants/routes';
import { TabNavigationProp } from '../shared/types/RootStackParamList';
import { CustomIcon } from '../components/CustomIcon';
import { ModalType, useModalStore } from '../store/modalStore';

import {
  ANALYTICS_EVENT,
  ACTION_TYPE,
} from '../analytics/types';
import { trackInteraction } from '../analytics/analyticsUtil';

export const MenuScreen = () => {
  const { theme, isDark, toggleTheme } = useThemeSettings();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const { t } = useTranslation();

  const navigation = useNavigation<TabNavigationProp>();
  const setModalShown = useModalStore((s) => s.setModalShown);
  const { user, setUser } = useUserStore();

  const [activeSection, setActiveSection] = useState('');

  const handleToggleTheme = () => {
    toggleTheme();

    trackInteraction({
      event: ANALYTICS_EVENT.INTERACTION,
      action: ACTION_TYPE.CLICK,
      screen_name: ROUTES.PROFILE,
      component: 'THEME_SWITCH',
      context: {
        next_theme: isDark ? 'light' : 'dark',
      },
    });
  };

  const onLogin = useCallback(() => {
    navigation.navigate(ROUTES.LOGIN as never);
  }, []);

  const redirectComingSoon = useCallback(() => {
    navigation.navigate(ROUTES.TBD as never);
  }, []);
  const getUsername = useCallback(() => {
    if (user?.thinkoUser?.provider !== 'anonymous') {
      return user?.thinkoUser?.name || user?.thinkoUser?.email;
    }
    return undefined;
  }, [user]);
  const navs = [
    {
      key: 'account',
      label: getUsername() || t('screens.profile.signIn'),
      icon: 'account',
      route: ROUTES.LOGIN,
    },
    {
      key: 'settings',
      label: t('screens.profile.settings'),
      icon: 'cog-outline',
      route: ROUTES.SETTINGS,
    },
    {
      key: 'collections',
      label: t('screens.profile.collections'),
      icon: 'collections',
      modal: ModalType.MANAGE_COLLECTIONS,
    },
    /* {
      key: 'rate',
      label: t('screens.profile.rate'),
      icon: 'lightbulb-on-outline',
    },
    {
      key: 'share',
      label: t('screens.profile.share'),
      icon: 'share',
    }, */
    {
      key: 'contact',
      label: t('screens.profile.contact'),
      icon: 'phone',
      route: ROUTES.CONTACT_US,
    },
    {
      key: 'signout',
      label: t('screens.profile.signOut'),
      icon: 'logout',
      hide: !getUsername(),
      action: 'logout',
    },
  ];

  const navOnPress = async (nav: any) => {
    setActiveSection(nav.icon);
    setModalShown(ModalType.NONE);

    switch (nav.key) {
      case 'account':
        !getUsername() ? onLogin() : ()=>{};
        break;

      case 'settings':
        navigation.navigate(ROUTES.SETTINGS as never);
        break;

      case 'collections':
        setModalShown(ModalType.MANAGE_COLLECTIONS);
        break;

      case 'contact':
        navigation.navigate(ROUTES.CONTACT_US as never);
        break;

      case 'signout':
        await signOutUser();
        const userData = await bootstrapAuth()
        setUser(userData);
        break;

      default:
        redirectComingSoon();
    }
  };

  const isSectionActive = (nav: any) => nav.icon === activeSection;

  const getNavItem = (nav: any) => (
    <TouchableOpacity onPress={() => navOnPress(nav)}>
      <List.Item
        titleStyle={{ color: theme?.colors?.textPrimary }}
        style={{ paddingLeft: 20 }}
        title={nav.label}
        left={() => (
          <View
            style={[
              gStyles.buttonStyle,
              gStyles.buttonCircle,
              isSectionActive(nav)
                ? {
                  backgroundColor: theme?.colors?.primary,
                  padding: 8,
                }
                : { backgroundColor: 'transparent' },
            ]}
          >
            <CustomIcon
              icon={nav.icon}
              size={22}
              color={
                isSectionActive(nav)
                  ? theme?.colors?.white
                  : theme?.colors?.primary
              }
            />
          </View>
        )}
        right={() => (
          <List.Icon icon="chevron-right" color={theme?.colors.primary} />
        )}
      />
    </TouchableOpacity>
  );

  return (
    <SafeAreaView edges={['bottom']}
      style={[gStyles.fullWidth, { flex: 1 }]}>
      <View style={{ flex: 1 }}>
        <ScrollView showsVerticalScrollIndicator={Platform.OS === 'web'}
        >
          {navs.map(
            (nav) =>
              !nav.hide && (
                <View key={nav.key}>{getNavItem(nav)}</View>
              )
          )}
        </ScrollView>

        <View style={styles.themeContainer}>
          <List.Item
            titleStyle={{ color: theme?.colors?.textPrimary }}
            title={t('screens.profile.theme', {
              theme: isDark
                ? t('screens.profile.light')
                : t('screens.profile.dark'),
            })}
            left={() => (
              <CustomIcon icon="theme" color={theme?.colors?.primary} />
            )}
            right={() => (
               <View style={{ marginRight: -15}}>
                <Switch value={isDark} onValueChange={handleToggleTheme} />
              </View>
            )}
          />
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  themeContainer: {
    borderTopWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    padding: 10,
    
  },
});
